import { useMemo } from "react";
import { useLocation, useSearchParams } from "wouter";
import { useProducts, FILTER_CATEGORIES } from "@/features/marketplace";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import SearchBar from "@/components/ui/search-bar";
import { Filter, FilterHeader, FilterOption, FilterSection } from "@/components/ui/filter";
import { LoadingIndicator } from "@/components/ui/loading-indicator";
import useMusioApi from "@/contexts/client/musio-client-context";
import { multiSelect, norm, textSearch, useFilteredList, useFilterState } from "@/utils/filter";
import { Sidebar, SidebarContent, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { Helmet } from "@dr.pogodin/react-helmet";
import CollectionGrid from "@/features/marketplace/components/collection-grid";
import type { CollectionWithPrice } from "@/types/api";

type FilterState = {
  categories: string[];
  developers: string[];
  q: string;
};

export default function MarketplacePage() {
  const { musioApi } = useMusioApi();
  const isMobile = useIsMobile(1024);
  const [params, setParams] = useSearchParams();

  const [_, navigate] = useLocation();
  const { items, isCartItemsPending: productsLoading, isCartItemsError } = useProducts();

  const { state: filters, set: setFilter } = useFilterState<FilterState>({
    categories: params.get("categories")?.split(",") ?? [],
    developers: params.get("developers")?.split(",") ?? [],
    q: params.get("q") ?? "",
  });

  const { data: developers, isPending: isDevelopersPending, isError: isDeveloperError } = musioApi.useQuery("get", "/catalog/developer");
  const developerOptions = developers ? developers.map((d) => d.developerName) : [];

  const predicates = useMemo(() => {
    return [
      multiSelect(
        (item: CollectionWithPrice) => item.tags?.map((t: string) => norm(t)) ?? [],
        (s: FilterState) => s.categories.map(norm),
        true, // this one we want to have only results that have these instruements - example I want to see collection with strings and brass
      ),

      multiSelect(
        (item: CollectionWithPrice) => [item.developerName ?? ""],
        (s: FilterState) => s.developers,
        false, // we want the developers to show as multi hit - exmaple I want to see results from dev A AND dev B
        (v: string) => v, // exact
      ),

      textSearch<CollectionWithPrice, FilterState>(
        (s) => s.q,
        [
          (item) => item.cartItem?.productName || item.collectionName,
          (item) => item.tags?.join(" "),
        ],
      ),
    ];
  }, []);

  const filteredItems = useFilteredList(items, filters, predicates);

  const filterPanel = (
    <FilterPanel
      loading={isDevelopersPending}
      developerOptions={developerOptions}
      setCategories={(categories: string[]) => {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);

            if (categories.length) {
              next.set("categories", categories.join(","));
            } else {
              next.delete("categories");
            }

            return next;
          },
          { replace: true },
        );

        setFilter("categories")(categories);
      }}
      setDevelopers={(developers: string[]) => {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);

            if (developers.length) {
              next.set("developers", developers.join(","));
            } else {
              next.delete("developers");
            }

            return next;
          },
          { replace: true },
        );

        setFilter("developers")(developers);
      }}
      activeOptions={filters}
      isDeveloperError={isDeveloperError}
    />
  );

  return (
    <>
      <Helmet>
        <title>Musio Marketplace</title>
        <meta name="description" content="Shop for the world's best virtual instruments built for the Musio plugin" />
        {/*<meta name="keywords" content={items.map((i) => i.cartItem?.productName).join(",")} />*/}
      </Helmet >
      <SidebarProvider
        className="
          h-full gap-2
          grid grid-cols-1 grid-rows-[auto_1fr]
          lg:grid-cols-5 lg:grid-rows-1 
        "
        defaultOpen={false}
      >

        {isMobile ? (
          <Sidebar>
            <ScrollArea className="musio-glass h-full">
              <SidebarContent>
                {filterPanel}
              </SidebarContent>
              <ScrollBar orientation="vertical" />
            </ScrollArea>
          </Sidebar>
        ) : (
          <ScrollArea className="musio-glass hidden lg:block lg:col-span-1 min-h-0">
            <div className="overflow-auto">
              {filterPanel}
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
        )}

        <div className="musio-glass col-span-1 lg:col-span-4 min-w-0 min-h-0 flex flex-col w-full">
          <div className="px-6 pt-6 flex flex-row items-center shrink-0 gap-2">
            <SidebarTrigger className="lg:hidden" />
            <SearchBar
              value={filters.q}
              setSearch={(query) => {
                setParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);

                    if (query) {
                      next.set("q", query);
                    } else {
                      next.delete("q");
                    }

                    return next;
                  },
                  { replace: true },
                );

                setFilter("q")(query);
              }}
              className="bg-musio-bg-light"
              style={{ border: 'rgba(255, 255, 255, 0.2) 1px solid' }}
            />
          </div>

          <div className="min-h-0 p-6">
            {productsLoading ? (
              <div className="flex h-full w-full items-center justify-center">
                <LoadingIndicator />
              </div>
            ) : isCartItemsError ? (
              <div className="flex h-full w-full items-center justify-center text-center">
                <p className="text-musio-light-lilac">Something went wrong while loading the marketplace collection. Please try again later</p>
              </div>
            ) : (
              <ScrollArea className="h-full w-full border-t-2 border-musio-mid-gray">
                <div className="flex flex-wrap gap-4 justify-center">
                  {filteredItems.length > 0 ? (
                    filteredItems.map((item) => (
                      <CollectionGrid
                        key={item.collectionId}
                        collection={item}
                        onClick={() =>
                          navigate(
                            `/collection/${item.collectionSlug}/?filters=` +
                            encodeURIComponent(params.toString())
                          )
                        }
                      />
                    ))
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-center p-4">
                      <p className="text-musio-gray">No products found.</p>
                    </div>
                  )}
                </div>
                <ScrollBar orientation="vertical" />
                <ScrollBar orientation="vertical" />
              </ScrollArea>
            )}
          </div>
        </div>
      </SidebarProvider >
    </>
  );
}

function FilterPanel({
  loading,
  developerOptions,
  setCategories,
  setDevelopers,
  activeOptions,
  isDeveloperError,
}: {
  loading: boolean;
  developerOptions: string[];
  setCategories: (categories: string[]) => void,
  setDevelopers: (categories: string[]) => void,
  activeOptions: FilterState;
  isDeveloperError: boolean;
}) {
  return (
    <>
      <Filter multiSelect className="p-3" onFilterChange={setCategories} activeOptions={activeOptions.categories}>
        <FilterHeader className="px-3 pb-4">Categories</FilterHeader>
        <FilterSection>
          {FILTER_CATEGORIES.map((item, index) => (
            <FilterOption key={index} className="p-2">
              {item}
            </FilterOption>
          ))}
        </FilterSection>
      </Filter>

      {loading ? (
        <div className="w-full flex items-center justify-center">
          <LoadingIndicator className="px-3 py-4" />
        </div>
      ) : isDeveloperError ? (
        <div className="w-full flex items-center justify-center p-4">
          <p>Failed to load developer options. Please try again later.</p>
        </div>
      ) : (
        <Filter
          multiSelect
          className="p-3"
          onFilterChange={setDevelopers}
          activeOptions={activeOptions.developers}
        >
          <FilterHeader className="px-3 pb-4">Developer</FilterHeader>
          <FilterSection>
            {developerOptions.map((item, index) => (
              <FilterOption key={index} className="p-2">
                {item}
              </FilterOption>
            ))}
          </FilterSection>
        </Filter>
      )}
    </>
  );
}
